import type { Shipment, WorkflowStatusInfo } from "../types.js";

/**
 * Lógica PURA da Etapa 4 (etiquetas de caixa). Sem React/IO — testável com o
 * payload real da API.
 *
 * O ponto crítico: a Amazon divide o plano em N remessas e `getLabels` v0 é POR
 * remessa (`shipmentId` singular no path da SP-API v0) — são N PDFs distintos.
 * O Seller Central mostra um botão "Print box labels" em CADA remessa; o FiCC
 * mostrava um único cartão servindo `plan.boxLabelsR2Key`, que aponta só para o
 * 1º envio. Num plano dividido as etiquetas dos demais envios existiam no GCS
 * mas eram inalcançáveis, e aquelas caixas seguiriam SEM etiqueta.
 */

export const TERMINAL_WORKFLOW_STATUSES = new Set<string>([
  "FAILED",
  "TIMED_OUT",
  "CANCELLED"
]);

/**
 * Estado de prontidão das etiquetas de caixa:
 *  - checking:    primeira verificação em andamento;
 *  - preparing:   workflow rodando, etiquetas ainda não gravadas → poll;
 *  - partial:     PARTE dos envios já tem etiqueta (plano dividido) → segue
 *                 pollando mas já libera os botões prontos;
 *  - ready:       todos os envios com etiqueta → botões habilitados;
 *  - unavailable: workflow terminou (COMPLETED ou falhou/cancelou) sem etiquetas;
 *  - stalled:     o poll esgotou sem resolver (Amazon demorando) → permite rechecar.
 */
export type BoxState =
  | "checking"
  | "preparing"
  | "partial"
  | "ready"
  | "unavailable"
  | "stalled";

/**
 * Prontidão da etiqueta de caixa a partir do status do workflow. Usa a flag
 * autoritativa `boxLabelsReady` quando presente; se ausente (API anterior ao
 * deploy da flag), faz fallback conservador para `workflowStatus === COMPLETED`.
 */
export function deriveBoxReady(status: WorkflowStatusInfo): boolean {
  if (typeof status.boxLabelsReady === "boolean") {
    return status.boxLabelsReady;
  }
  return status.workflowStatus === "COMPLETED";
}

/**
 * Este envio já tem etiqueta? Usa a flag per-shipment quando a API a expõe; sem
 * ela (API anterior) cai no sinal do PLANO, que só é verdadeiro para o 1º envio
 * — conservador, mas nunca habilita um botão que responderia 404.
 */
export function shipmentReady(
  shipment: Shipment,
  index: number,
  status: WorkflowStatusInfo | null
): boolean {
  if (typeof shipment.boxLabelsReady === "boolean") {
    return shipment.boxLabelsReady;
  }
  return index === 0 && status !== null && deriveBoxReady(status);
}

/**
 * Classifica a prontidão considerando TODOS os envios. Com a lista de envios
 * disponível ela é a fonte autoritativa (o campo do plano só enxerga o 1º);
 * sem envios (pré-finalize / API antiga) cai no sinal do plano.
 */
export function classifyBox(
  status: WorkflowStatusInfo,
  shipments: readonly Shipment[]
): BoxState {
  const terminal =
    TERMINAL_WORKFLOW_STATUSES.has(status.workflowStatus) ||
    status.workflowStatus === "COMPLETED";

  if (shipments.length > 0) {
    const readyCount = shipments.filter((s, i) =>
      shipmentReady(s, i, status)
    ).length;
    if (readyCount === shipments.length) {
      return "ready";
    }
    if (readyCount > 0) {
      // Parte dos envios já tem etiqueta: esses botões ficam habilitados enquanto
      // os demais continuam sendo gerados (ou seguem faltando, se já terminou).
      return "partial";
    }
    return terminal ? "unavailable" : "preparing";
  }

  if (deriveBoxReady(status)) {
    return "ready";
  }
  if (terminal) {
    return "unavailable";
  }
  return "preparing";
}

/** Chave do cartão do PLANO (fallback single-shipment / API antiga). */
export const PLAN_CARD_KEY = "__plano__";

export interface BoxLabelCard {
  chave: string;
  /** `null` ⇒ baixa pelo path do plano (compatibilidade). */
  shipmentId: string | null;
  titulo: string;
  /** Identidade da remessa (FC · FBAxxxx · nome) — null quando não há dados. */
  subtitulo: string | null;
  pronto: boolean;
}

/**
 * Monta UM cartão por envio (espelha o Seller Central). Sem envios (pré-finalize
 * ou API antiga) devolve o cartão único do plano, preservando o comportamento
 * anterior.
 */
export function buildBoxLabelCards(
  shipments: readonly Shipment[],
  status: WorkflowStatusInfo | null,
  boxState: BoxState
): BoxLabelCard[] {
  if (shipments.length === 0) {
    return [
      {
        chave: PLAN_CARD_KEY,
        shipmentId: null,
        titulo: "Etiquetas de caixa — Envio Nº 1",
        subtitulo: null,
        pronto: boxState === "ready"
      }
    ];
  }

  return shipments.map((shipment, index) => ({
    chave: shipment.shipmentId,
    shipmentId: shipment.shipmentId,
    titulo:
      shipments.length > 1
        ? `Etiquetas de caixa — Envio Nº ${index + 1} de ${shipments.length}`
        : "Etiquetas de caixa — Envio Nº 1",
    subtitulo: descreveEnvio(shipment),
    pronto: shipmentReady(shipment, index, status)
  }));
}

/** Identidade do envio para o operador casar a etiqueta com a remessa certa. */
export function descreveEnvio(shipment: Shipment): string | null {
  const partes: string[] = [];
  if (
    shipment.destinationFcCode !== null &&
    shipment.destinationFcCode.length > 0
  ) {
    partes.push(`Destino ${shipment.destinationFcCode}`);
  }
  if (
    shipment.shipmentConfirmationId !== null &&
    shipment.shipmentConfirmationId.length > 0
  ) {
    partes.push(shipment.shipmentConfirmationId);
  }
  if (shipment.name !== null && shipment.name.length > 0) {
    partes.push(shipment.name);
  }
  return partes.length > 0 ? partes.join(" · ") : null;
}
